import { LocationAutocomplete, type LocationResult } from '@/components/location-autocomplete';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { type SelectedLocation } from '@/types';
import { MapPin, X } from 'lucide-react';
import { useState } from 'react';

interface LocationFilterFieldProps {
    searchType: 'embarcacoes' | 'empresas';
    location: SelectedLocation | null;
    radius: number | null;
    radiusOptions: number[];
    onLocationChange: (location: SelectedLocation | null) => void;
    onRadiusChange: (radius: number) => void;
    className?: string;
}

export const formatRadiusLabel = (radius: number) => (radius === 0 ? 'Somente na cidade' : `Até ${radius} km`);

/** Raio só faz sentido a partir de uma cidade com coordenada. */
export const locationSupportsRadius = (location: SelectedLocation | null) => location?.type === 'city' && location.has_coordinates !== false;

export const toSelectedLocation = (result: LocationResult): SelectedLocation => {
    if (result.type === 'state') {
        return {
            type: 'state',
            id: result.id,
            label: result.code ? `${result.name} - ${result.code}` : result.name,
            slug: result.slug ?? null,
        };
    }

    return {
        type: 'city',
        id: result.id,
        label: result.display,
        slug: result.slug ?? null,
        has_coordinates: result.has_coordinates ?? false,
    };
};

/**
 * Filtro de localização das buscas: campo "Cidade ou estado" com sugestões; escolhida a
 * localização, vira um chip removível. Para cidade, o seletor de raio aparece ao lado.
 */
export function LocationFilterField({
    searchType,
    location,
    radius,
    radiusOptions,
    onLocationChange,
    onRadiusChange,
    className,
}: LocationFilterFieldProps) {
    const [query, setQuery] = useState('');
    const showRadius = locationSupportsRadius(location) && radius !== null && radiusOptions.length > 1;

    if (!location) {
        return (
            <LocationAutocomplete
                value={query}
                searchType={searchType}
                placeholder="Cidade ou estado"
                className={className}
                inputClassName="h-11 sm:h-11 sm:pl-9 sm:text-sm"
                onChange={(value, result) => {
                    if (result) {
                        setQuery('');
                        onLocationChange(toSelectedLocation(result));
                        return;
                    }

                    setQuery(value);
                }}
            />
        );
    }

    return (
        <div className={cn('flex flex-col gap-2', className)}>
            <div className="flex min-w-0 items-center rounded-full border border-blue-800 bg-blue-50 pl-3 text-sm text-blue-800">
                <MapPin aria-hidden="true" className="h-4 w-4 shrink-0" />
                <span className="ml-1.5 min-w-0 flex-1 truncate font-medium" title={location.label}>
                    {location.label}
                </span>
                <button
                    type="button"
                    onClick={() => onLocationChange(null)}
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-blue-800 transition-colors hover:bg-blue-100 hover:text-blue-900 lg:h-9 lg:w-9"
                    aria-label={`Remover localização ${location.label}`}
                >
                    <X aria-hidden="true" className="h-4 w-4" />
                </button>
            </div>

            {showRadius && (
                <Select value={String(radius)} onValueChange={(value) => onRadiusChange(Number(value))}>
                    <SelectTrigger aria-label="Raio de distância" className="h-11 w-full bg-white lg:h-9">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {radiusOptions.map((option) => (
                            <SelectItem key={option} value={String(option)} className="min-h-11 lg:min-h-8">
                                {formatRadiusLabel(option)}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            )}
        </div>
    );
}

/**
 * Raio depois de trocar a localização: cidade → cidade mantém o raio escolhido; vindo de
 * estado/nenhuma, usa o padrão do módulo. Estado, sem localização ou cidade sem coordenada: sem raio.
 */
export const resolveNextRadius = (
    previousLocation: SelectedLocation | null,
    previousRadius: number | null,
    nextLocation: SelectedLocation | null,
    defaultRadius: number,
): number | null => {
    if (!locationSupportsRadius(nextLocation)) {
        return null;
    }

    if (previousLocation?.type === 'city' && previousRadius !== null) {
        return previousRadius;
    }

    return defaultRadius;
};
